import { SelectedMinerI } from "@store/minersSelect/actionTypes";

import styles from "./index.module.scss";
import WithModal, { CommonModalPropsI } from "@components/HOC/withModals";
import { MODALS_ENUM } from "@constants/Modals";
import Title from "@components/global/new/Title/Title";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { show } from "redux-modal";
import { Dispatch } from "redux";
import cn from "classnames";
import { useMe } from "@utils/hooks/useMe";
import { useMinerLocationsRaw } from "@utils/hooks/useMinerLocations";
import { getDevivedHash } from "@utils/hash.utils";
import moment from "moment";
import { DeviceI, getDevices } from "@utils/dev_utils";
import { useMinerProfiles } from "@utils/hooks/useMinerProfiles";
import { useContracts } from "@utils/hooks/useContracts";
import { Button } from "antd";
import { useUsers } from "@utils/hooks/useUsers";

export interface MiningPercentI {
  percent: number;
}

interface ParamTraitsI {
  name?: string;
  id?: string;
  modif?: (v: any) => string;
}

const percentModif = (v: any) => `${v}%`;

const paramsKeysReadableNames: Record<string, ParamTraitsI> = {
  percent: { id: "machines.settings.admin_income", modif: percentModif },
  miner_plan: { id: "machines.settings.plan" },
  speed_drops: {
    id: "machines.settings.speed_drop_notif",
    modif: percentModif,
  },
  speed_drop: { id: "machines.settings.speed_drop_notif", modif: percentModif },
  notifications: { id: "machines.settings.notify_by" },
  pool_id: { id: "machines.settings.pool_name" },
  miner_smooth: {
    id: "machines.smooth_switch",
    modif: (v: any) => (v ? "1" : "0"),
  },
  user_note: { id: "machines.more.settings.personal_note" },
  customer: {
    id: "machines.table.customer",
    modif: (v: any) =>
      typeof v === "string" && v[0] === "{" ? JSON.parse(v).email : `${v}`,
  },
  miner_type: { id: "machines.table.type" },
  default_speed: {
    id: "machines.settings.speed_default",
    modif: getDevivedHash,
  },
  mining_persent: { id: "machines.settings.admin_income", modif: percentModif },
  admin_note: { id: "machines.more.settings.personal_note" },
  miner_id: { id: "machines.settings.miner_name" },
  id: { id: "machines.settings.miner_id" },
  location_id: { id: "machines.settings.location" },
  sn: { id: "machines.more.serial.title" },
  pn: { id: "machines.more.model.title" },
  profile_id: { id: "machines.settings.type" },
  contractid: { id: "contracts.selector.lable" },
  set_install_time: {
    id: "machines.more.instalation_date.title",
    modif: (v: any) => ((v && !isNaN(v)) ? moment(+v as number).format("DD-MM-YYYY") : (v || "")),
  },
};

interface ConfirmMinersChangesPropsI {
  params: Record<string, any>;
  selected: SelectedMinerI[];

  title?: string;
  subtitle?: string;

  onConfirm(): void;
  onCancel?(): void;
}

function ConfirmMinersChanges({
  title,

  handleClose,
  onCancel,
  onConfirm,
  formatMessage,
  ...props
}: ConfirmMinersChangesPropsI & CommonModalPropsI) {
  const me = useMe();
  const locations = useMinerLocationsRaw();
  const onConfirmHandler = () => {
    onConfirm();
    handleClose();
  };
  const minerProfiles = useMinerProfiles();
  const contracts = useContracts();
  const allUsers = useUsers();

  const onCancelHandler = () => {
    onCancel && onCancel();
    handleClose();
  };

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current && ref.current.parentElement) {
      ref.current.parentElement.style.width = "500px";
    }
  }, [ref]);

  const fmt = (id: string) => formatMessage({ id }).replace(/\:\s*$/, "");
  const findPoolSetting = useCallback(
    (id: any) =>
      me?.settings
        ? me.settings.find((s) => +s.id === +id)?.name || ""
        : `${id}`,
    [me?.settings]
  );
  const findLocationByID = useCallback(
    (id: any) =>
      locations.find((loc) => `${loc.id}` === `${id}`)?.name || `${id}`,
    [locations]
  );
  const paramsNames: Record<string, ParamTraitsI> = useMemo(
    () => ({
      ...paramsKeysReadableNames,
      pool_id: { ...paramsKeysReadableNames.pool_id, modif: findPoolSetting },
      location_id: {
        ...paramsKeysReadableNames.location_id,
        modif: findLocationByID,
      },
      profile_id: {
        ...paramsKeysReadableNames.profile_id,
        modif(v: any) {
          const p = minerProfiles.find(mp => mp.id === +v)
          return p?.name ?? "";
        }
      },
      contractid: {
        ...paramsKeysReadableNames.contractid,
        modif(v: any) {
          const p = contracts.find(mp => mp.id === +v);
          return p?.name ?? "";
        }
      },
      customer: {
        ...paramsKeysReadableNames.customer,
        modif: (v: any) => {
          if (typeof v === "string" && v[0] === "{") {
            return JSON.parse(v).email;
          } else {
            const user = allUsers.find(user => user.id === v);
            return user?.name || user?.email || `${v}`;
          }
        },
      },
      miner_plan: {
        ...paramsKeysReadableNames.miner_plan,
        modif: (v: any) => {
          const plansRaw = contracts.find(mp => mp.id === +props.params.contractid)?.plans;
          if (plansRaw) {
            const plans = JSON.parse(plansRaw);
            if (Array.isArray(plans)) {
              return plans.find(plan => plan.id === +v)?.name || v;
            } else {
              return "-";
            }
          } else {
            return v;
          }
        },
      },
    }),
    [findPoolSetting]
  );

  const cellContents = useCallback(
    (key: string) =>
      paramsNames[key]?.modif
        ? paramsNames[key].modif!(props.params[key])
        : props.params[key],
    [paramsNames, props.params]
  );

  return (
    <div className={styles.wrapper} ref={ref}>
      <Title className={styles.title}>
        {title || formatMessage({ id: "modal.confirm.title" })}
      </Title>
      {props.subtitle && (
        <Title className={styles.subtitle}>{props.subtitle}</Title>
      )}

      <div className={styles.changesInfo}>
        <label>
          {formatMessage({ id: "machines.settings.num_miners" }).replace(
            "#",
            `${props.selected?.length || 0}`
          )}
        </label>
        <div className={styles.table}>
          {props.params &&
            Object.keys(props.params)
              .filter((k) => k !== "miner")
              .map(
                (key) =>
                  props.params[key] !== undefined &&
                  Object.hasOwn(paramsNames, key) && (
                    <div className={styles.tableRow} key={key}>
                      <div
                        className={cn(styles.tableCell, styles.tableRowName)}
                      >
                        <div className={styles.cellContents}>
                          {paramsNames[key].id
                            ? fmt(paramsNames[key].id!)
                            : paramsNames[key].name}
                        </div>
                      </div>
                      <div
                        className={cn(styles.tableCell, styles.tableRowValue)}
                      >
                        <div
                          className={styles.cellContents}
                          title={cellContents(key)}
                        >
                          {cellContents(key)}
                        </div>
                      </div>
                    </div>
                  )
              )}
        </div>
      </div>

      <div className={styles.btns}>
        <Button type="primary" onClick={onConfirmHandler}>
          {formatMessage({ id: "modal.confirm.btn.yes" })}
        </Button>
        <Button onClick={onCancelHandler}>
          {formatMessage({ id: "modal.confirm.btn.no" })}
        </Button>
      </div>
    </div>
  );
}

export default WithModal(
  MODALS_ENUM.CONFIRM_MINERS_CHANGES_MODAL,
  ConfirmMinersChanges,
  {
    style: {
      width: "500px",
    },
  }
);

export const confirmMinersChanges = (
  dispatch: Dispatch,
  params: Record<string, any>,
  selected: SelectedMinerI[]
): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    const props: ConfirmMinersChangesPropsI & { onClosing: () => void } = {
      params,
      selected,
      onConfirm() {
        resolve(true);
      },
      onCancel() {
        resolve(false);
      },
      onClosing() {
        resolve(false);
      },
    };
    dispatch(show(MODALS_ENUM.CONFIRM_MINERS_CHANGES_MODAL, props));
  });
};
